import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BellIcon, CheckCheckIcon, SettingsIcon } from 'lucide-react';
import {
  getListMyNotificationsQueryKey,
  getGetMyNotificationUnreadCountQueryKey,
  useGetMyNotificationUnreadCount,
  useListMyNotifications,
  useMarkAllMyNotificationsRead,
  useMarkMyNotificationRead,
  type NotificationItem
} from '@cdf/api-client-react';

import { useCustomerSkin } from '@/components/tenancy/customer-brand-provider';
import { useSession } from '@/components/auth/user-provider';
import { selectedEntity } from '@/features/tenancy/context-navigation';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

function relativeTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const diff = Date.now() - ms;
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function NotificationRow({
  item,
  onOpen
}: {
  item: NotificationItem;
  onOpen: (item: NotificationItem) => void;
}) {
  const unread = !item.readAt;
  return (
    <DropdownMenuItem
      className={cn(
        'flex cursor-pointer flex-col items-start gap-0.5 whitespace-normal py-2',
        unread && 'bg-accent/40'
      )}
      onSelect={(e) => {
        e.preventDefault();
        onOpen(item);
      }}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <span className={cn('text-sm', unread ? 'font-semibold' : 'font-medium')}>{item.title}</span>
        <span className="text-muted-foreground shrink-0 text-[11px]">
          {relativeTime(item.createdAt)}
        </span>
      </div>
      {item.body ? (
        <span className="text-muted-foreground line-clamp-2 text-xs leading-snug">{item.body}</span>
      ) : null}
    </DropdownMenuItem>
  );
}

/**
 * Header notification center: unread badge + list with mark-as-read and deep links.
 */
export function NotificationBell() {
  const { showNotificationIcon, contextKey } = useCustomerSkin();
  return showNotificationIcon ? <VisibleNotificationBell key={contextKey} /> : null;
}

function VisibleNotificationBell() {
  const { session } = useSession();
  const signedIn = session.status === 'signedIn';
  const { pathname } = useLocation();
  const entity = selectedEntity(signedIn ? session.user.tenants : [], pathname);
  const available = signedIn && entity?.status === 'active';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const listParams = { limit: 20 } as const;

  const unreadQ = useGetMyNotificationUnreadCount({
    query: {
      queryKey: getGetMyNotificationUnreadCountQueryKey(),
      enabled: available,
      refetchInterval: available ? 45_000 : false,
      staleTime: 15_000
    }
  });

  const listQ = useListMyNotifications(listParams, {
    query: {
      queryKey: getListMyNotificationsQueryKey(listParams),
      enabled: available && open,
      staleTime: 10_000
    }
  });

  const markRead = useMarkMyNotificationRead({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          qc.invalidateQueries({ queryKey: getListMyNotificationsQueryKey(listParams) }),
          qc.invalidateQueries({ queryKey: getGetMyNotificationUnreadCountQueryKey() })
        ]);
      }
    }
  });

  const markAll = useMarkAllMyNotificationsRead({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          qc.invalidateQueries({ queryKey: getListMyNotificationsQueryKey(listParams) }),
          qc.invalidateQueries({ queryKey: getGetMyNotificationUnreadCountQueryKey() })
        ]);
      }
    }
  });

  const unreadCount = unreadQ.data?.unreadCount ?? listQ.data?.unreadCount ?? 0;
  const items = listQ.data?.items ?? [];
  const badge = useMemo(() => {
    if (unreadCount <= 0) return null;
    return unreadCount > 99 ? '99+' : String(unreadCount);
  }, [unreadCount]);

  if (!available) return null;

  async function onOpen(item: NotificationItem) {
    if (!item.readAt) {
      try {
        await markRead.mutateAsync({ id: item.id });
      } catch {
        // Soft-fail — still navigate when linked.
      }
    }
    if (item.href) {
      setOpen(false);
      if (item.href.startsWith('/')) {
        navigate(item.href);
      }
    }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
          }
          className="relative"
        >
          <BellIcon className="size-5" />
          {badge ? (
            <span className="bg-destructive text-destructive-foreground absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none">
              {badge}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={unreadCount === 0 || markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              <CheckCheckIcon className="mr-1 size-3.5" />
              Mark all read
            </Button>
            <Button type="button" variant="ghost" size="icon" className="size-7" asChild>
              <Link to="/settings" aria-label="Notification settings">
                <SettingsIcon className="size-3.5" />
              </Link>
            </Button>
          </div>
        </div>
        <DropdownMenuSeparator className="m-0" />
        <div className="max-h-80 overflow-y-auto py-1">
          {listQ.isLoading ? (
            <p className="text-muted-foreground px-3 py-6 text-center text-sm">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-muted-foreground px-3 py-6 text-center text-sm">
              No notifications yet.
            </p>
          ) : (
            items.map((item) => (
              <NotificationRow key={item.id} item={item} onOpen={onOpen} />
            ))
          )}
        </div>
        {unreadQ.data?.unreadCount != null && unreadQ.data.unreadCount > items.length ? (
          <>
            <DropdownMenuSeparator className="m-0" />
            <p className="text-muted-foreground px-3 py-2 text-center text-[11px]">
              Showing latest {items.length}. Older items stay unread until opened.
            </p>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
